import { createContext, useContext, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import styled, { useTheme } from 'styled-components';

const DEFAULT_CENTER = { lat: 41.9168256, lng: -87.6969984 };
const DEFAULT_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

const BlankGridLayer = L.GridLayer.extend({
  createTile() {
    const tile = document.createElement('canvas');
    tile.width = 256;
    tile.height = 256;
    const ctx = tile.getContext('2d');
    ctx.fillStyle = this.options.bg || '#f8f8f8';
    ctx.fillRect(0, 0, 256, 256);
    return tile;
  },
});

export const MapInstanceContext = createContext(null);
export const useMapInstance = () => useContext(MapInstanceContext);

const LeafletContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const LeafletMap = ({
  center = DEFAULT_CENTER,
  zoom = 17,
  tileUrl = DEFAULT_TILE_URL,
  overlayColor = null,
  overlayBlendMode = 'multiply',
  options = {},
  children,
}) => {
  const divRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const { colors } = useTheme();
  const blankBg = colors('background');

  useEffect(() => {
    if (!divRef.current) return;
    const map = L.map(divRef.current, {
      center: [center.lat, center.lng],
      zoom,
      zoomControl: false,
      attributionControl: true,
      ...options,
    });
    setMapInstance(map);
    return () => { map.remove(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!mapInstance) return;
    const layer = tileUrl === 'blank'
      ? new BlankGridLayer({ maxZoom: 19, bg: blankBg })
      : tileUrl
        ? L.tileLayer(tileUrl, { maxZoom: 19, attribution: '© OpenStreetMap contributors © CARTO' })
        : null;
    if (layer) layer.addTo(mapInstance);
    return () => { if (layer) mapInstance.removeLayer(layer); };
  }, [mapInstance, tileUrl, blankBg]);

  return (
    <MapInstanceContext.Provider value={mapInstance}>
      <LeafletContainer>
        <div ref={divRef} style={{ position: 'absolute', inset: 0 }} />
        {overlayColor && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: overlayColor,
              mixBlendMode: overlayBlendMode,
              pointerEvents: 'none',
              zIndex: 400,
            }}
          />
        )}
        {mapInstance && children}
      </LeafletContainer>
    </MapInstanceContext.Provider>
  );
};
